import {useCallback, useState} from 'react';
import {isLocalEntityRef} from '../../utils/entityLinks';

const QID_RE = /^Q\d+$/i;

function isLinkedEntity(entity) {
    if (!entity) return false;
    const candidates = [entity.wikidataId, entity.entityId, entity.localEntityRef, entity.id];
    return candidates.some((c) => {
        if (c == null) return false;
        const s = String(c).trim();
        return QID_RE.test(s) || isLocalEntityRef(s);
    });
}

/**
 * Custom hook for the corpus/document entity interaction.
 *
 * Unified mention behaviour (matching reports & data items):
 * - Clicking a LINKED (blue) entity opens the entity info dialog.
 * - Clicking an UNLINKED (yellow) mention opens the Wikidata search dialog DIRECTLY
 *   (no intermediate dialog); selecting an entity persists the link via the caller-provided
 *   `onLink` handler.
 *
 * @param {Function} [onLink] async ({entity, wikidataEntity}) => persisted result
 * @returns interaction state + handlers
 */
export function useEntityDialog(onLink) {
    const [entityDialogState, setEntityDialogState] = useState({
        open: false,
        anchorEl: null,
        entity: null
    });
    // Pending mention awaiting a Wikidata selection (the clicked unlinked entity).
    const [searchState, setSearchState] = useState({open: false, entity: null, query: ''});

    const handleEntityClick = useCallback((entity, anchorEl) => {
        if (isLinkedEntity(entity)) {
            setEntityDialogState({open: true, anchorEl, entity});
            return;
        }
        // Unlinked mention → open Wikidata search directly.
        setSearchState({
            open: true,
            entity,
            query: entity?.canonicalName || entity?.mentionText || '',
        });
    }, []);

    const handleCloseEntityDialog = useCallback(() => {
        setEntityDialogState({open: false, anchorEl: null, entity: null});
    }, []);

    const handleCloseSearch = useCallback(() => {
        setSearchState({open: false, entity: null, query: ''});
    }, []);

    const handleSelectSearchEntity = useCallback(async (selected) => {
        const pending = searchState.entity;
        setSearchState({open: false, entity: null, query: ''});
        if (!pending || !onLink) return;
        try {
            await onLink({entity: pending, wikidataEntity: selected});
        } catch (err) {
            console.error('[useEntityDialog] manual link failed:', err);
        }
    }, [onLink, searchState.entity]);

    return {
        entityDialogState,
        handleEntityClick,
        handleCloseEntityDialog,
        searchState,
        handleCloseSearch,
        handleSelectSearchEntity,
    };
}
